import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Teacher-friendly progress UI — 2026-07-29 redesign.
 *
 * Stage truth (new): the poll response surfaces the backend's real
 * `current_stage` (generationFlow onPoll → pollState.currentStage). We map it
 * to the friendly stage list and take MAX(timerStage, realStage) — the timer
 * is the floor for old workers / early polls, the backend can only move the
 * label forward. The artist↔critic refinement loop maps to a round note
 * instead of moving the label backwards.
 *
 * The visual anchor is a poster-shaped frame (2:3) with a soft shimmer — the
 * eye waits on the thing it's actually waiting for. Rotating tips keep the
 * 2–5 min run company. "Saving to your library" still requires the REAL
 * `finalising` signal from the parent — never the timer.
 *
 * pollState is accepted for `currentStage` + failure detection only — no
 * technical fields (queue status, poll counts) are ever shown.
 */

// Timer delays reflect a "typical" 2–5 min run; for slower runs the timer
// caps at "finishing" and stays there until the orchestrator signals
// `finalising`. With a picked standard the JIT-enrichment stage adds 45–65 s
// on first-ever generation against a concept — delays shift accordingly.
const buildStages = (standardCode, t) => [
  { key: 'thinking',  emoji: '🌱', label: t('progress.stages.thinking'),  delay:     0 },
  ...(standardCode ? [{ key: 'teaching', emoji: '📚', label: t('progress.stages.teaching', { code: standardCode }), delay: 8000 }] : []),
  { key: 'designing', emoji: '🎨', label: t('progress.stages.designing'), delay: standardCode ? 70000 : 15000 },
  { key: 'creating',  emoji: '✏️', label: t('progress.stages.creating'),  delay: standardCode ? 100000 : 45000 },
  { key: 'polishing', emoji: '🔍', label: t('progress.stages.polishing'), delay: standardCode ? 160000 : 105000 },
  { key: 'finishing', emoji: '✨', label: t('progress.stages.finishing'), delay: standardCode ? 205000 : 150000 },
  { key: 'saving',    emoji: '💾', label: t('progress.stages.saving') }, // only via `finalising` prop
];

// Backend `current_stage` → friendly stage key. Artist/critic rounds also
// yield a round number for the quality-pass note (round ≥ 2 only).
function mapBackendStage(currentStage, hasStandard) {
  if (!currentStage) return { key: null, round: null };
  const s = String(currentStage);
  if (s.startsWith('curriculum')) return { key: hasStandard ? 'teaching' : 'thinking', round: null };
  if (s === 'idea' || s === 'architect') return { key: 'designing', round: null };
  const artist = s.match(/^artist-round-(\d+)/);
  if (artist) return { key: 'creating', round: Number(artist[1]) };
  const critic = s.match(/^critic-round-(\d+)/);
  if (critic) return { key: 'polishing', round: Number(critic[1]) };
  if (s === 'educator') return { key: 'polishing', round: null };
  if (s === 'upscale' || s === 'post-process' || s === 'upload' || s === 'catalog') {
    return { key: 'finishing', round: null };
  }
  return { key: null, round: null };
}

// Pushed back from 5/7 min to 7/10 min — worst-case pipeline (multiple
// critic-rejected artist rounds) is ~12-13 min; flashing "slow" warnings at
// 5 min trained users to expect failure too early.
const SLOW_AFTER_S = 7 * 60;
const VERY_SLOW_AFTER_S = 10 * 60;

const TIP_KEYS = ['tip1', 'tip2', 'tip3', 'tip4'];
const TIP_INTERVAL_MS = 11000;

export default function GenerationProgress({ pollState, finalising = false, onCancel, onBackground, startedAt = null, standardCode = null }) {
  const { t } = useTranslation('home');
  const [timerIdx, setTimerIdx] = useState(0);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [tipIdx, setTipIdx] = useState(0);
  const startRef = useRef(startedAt || Date.now());
  // Stage list is stable for the lifetime of one generation run.
  const stagesRef = useRef(buildStages(standardCode, t));
  const STAGES = stagesRef.current;
  // Timer is only allowed to advance this far. "Saving" requires a real signal.
  const SIM_CAP_INDEX = STAGES.length - 2; // finishing

  useEffect(() => {
    const id = setInterval(() => {
      setElapsedSec(Math.floor((Date.now() - startRef.current) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  // Timer-based stage floor, capped at SIM_CAP_INDEX.
  useEffect(() => {
    const timers = STAGES.slice(0, SIM_CAP_INDEX + 1).map((s, i) =>
      setTimeout(() => setTimerIdx(i), s.delay)
    );
    return () => { timers.forEach(clearTimeout); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rotating tips.
  useEffect(() => {
    const id = setInterval(() => setTipIdx((i) => (i + 1) % TIP_KEYS.length), TIP_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  // Real backend stage — can only move the label FORWARD past the timer.
  const backend = mapBackendStage(pollState?.currentStage, !!standardCode);
  const backendIdx = backend.key ? STAGES.findIndex((s) => s.key === backend.key) : -1;
  let stageIdx = Math.max(timerIdx, backendIdx);
  if (finalising) stageIdx = STAGES.length - 1;

  const serverFailed = pollState?.queueStatus === 'failed' || pollState?.posterStatus === 'rejected';

  const current = serverFailed
    ? { emoji: '😔', label: t('progress.failed') }
    : STAGES[stageIdx];

  // Quality-pass note: visible from the second artist/critic round on — the
  // difference between "it keeps improving my poster" and "it's frozen".
  const roundNote = !serverFailed && !finalising && backend.round && backend.round >= 2
    ? t('progress.qualityPass', { round: backend.round })
    : null;

  const mm = String(Math.floor(elapsedSec / 60)).padStart(2, '0');
  const ss = String(elapsedSec % 60).padStart(2, '0');

  const slow = !finalising && !serverFailed && elapsedSec >= SLOW_AFTER_S && elapsedSec < VERY_SLOW_AFTER_S;
  const verySlow = !finalising && !serverFailed && elapsedSec >= VERY_SLOW_AFTER_S;

  // Progress bar fills through the stages, then CREEPS from 85% toward 93% on
  // long runs (a hard pin reads as frozen). Jumps to 100% on finalising.
  const lastTimerDelayS = (STAGES[SIM_CAP_INDEX].delay ?? 150000) / 1000;
  const creep = elapsedSec > lastTimerDelayS
    ? Math.min(8, (elapsedSec - lastTimerDelayS) / 45)
    : 0;
  const progressPct = finalising
    ? 100
    : Math.min(93, Math.round(((stageIdx + 1) / (STAGES.length - 1)) * 85 + creep));

  return (
    <div style={{
      fontFamily: 'inherit',
      padding: '28px 24px',
      maxWidth: 560, margin: '0 auto',
      color: 'rgba(27,37,80,0.855)',
    }}>
      {/* Poster-shaped frame — the eye waits on the shape of the outcome. */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
        <div aria-hidden="true" style={{
          width: 168, height: 252, borderRadius: 12, position: 'relative',
          border: '1px solid rgba(27,37,80,0.085)',
          background: 'linear-gradient(160deg, rgba(27,37,80,0.048), rgba(27,37,80,0.019))',
          overflow: 'hidden',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 12px 40px rgba(27,37,80,0.12)',
        }}>
          <div className="pgShimmer" />
          <div style={{ fontSize: '2.2rem', position: 'relative' }}>{current.emoji}</div>
        </div>
      </div>

      {/* Current stage */}
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <div style={{ fontSize: '1.08rem', fontWeight: 700, marginBottom: 5 }} aria-live="polite">
          {current.label}{!serverFailed && '…'}
        </div>
        {roundNote && (
          <div style={{ fontSize: '0.8rem', color: 'rgba(127,75,246,0.85)', marginBottom: 5 }}>
            {roundNote}
          </div>
        )}
        <div style={{ fontSize: '0.78rem', color: 'rgba(27,37,80,0.427)' }}>
          {t('progress.elapsed', { minutes: mm, seconds: ss })}
        </div>
      </div>

      {/* Progress bar */}
      <div style={{
        height: 8, borderRadius: 999, background: '#FFFFFF',
        overflow: 'hidden', marginBottom: 16,
      }}>
        <div style={{
          height: '100%', width: `${progressPct}%`,
          background: 'linear-gradient(90deg, rgba(127,75,246,0.9), rgba(127,75,246,0.9))',
          transition: 'width 0.8s cubic-bezier(.4,0,.2,1)',
        }} />
      </div>

      {/* Stage dots */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        {STAGES.map((s, i) => {
          const done = i < stageIdx;
          const active = i === stageIdx;
          return (
            <div key={s.key} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flex: 1, opacity: done || active ? 1 : 0.3,
            }}>
              <div style={{
                width: 12, height: 12, borderRadius: '50%',
                background: done ? 'rgba(127,75,246,0.9)' : active ? 'transparent' : 'rgba(27,37,80,0.142)',
                border: active ? '2px solid rgba(127,75,246,0.9)' : 'none',
                animation: active ? 'pulseDot 1.4s ease-in-out infinite' : 'none',
              }} />
            </div>
          );
        })}
      </div>

      {/* Rotating tip — quiet company for the wait. */}
      {!serverFailed && !slow && !verySlow && (
        <div key={tipIdx} className="pgTip" style={{
          textAlign: 'center', fontSize: '0.78rem', lineHeight: 1.5,
          color: 'rgba(27,37,80,0.399)', minHeight: 34, marginBottom: 10,
          padding: '0 12px',
        }}>
          {t(`progress.tips.${TIP_KEYS[tipIdx]}`)}
        </div>
      )}

      {/* Slow / timeout notes — friendly, no scary jargon */}
      {slow && (
        <div style={{
          padding: '10px 14px', borderRadius: 10, marginBottom: 14,
          background: '#FFFFFF', border: '1px solid rgba(27,37,80,0.076)',
          color: 'rgba(27,37,80,0.665)', fontSize: '0.82rem', lineHeight: 1.45,
          textAlign: 'center',
        }}>
          {t('progress.slowNote')}
        </div>
      )}
      {verySlow && (
        <div style={{
          padding: '10px 14px', borderRadius: 10, marginBottom: 14,
          background: 'rgba(127,75,246,0.08)', border: '1px solid rgba(127,75,246,0.25)',
          color: 'rgba(127,75,246,0.9)', fontSize: '0.82rem', lineHeight: 1.45,
          textAlign: 'center',
        }}>
          {t('progress.verySlowNote')}
        </div>
      )}

      {/* Continue in background (2026-09-28): the run keeps going when the
          teacher leaves; the finished poster is saved to the Library and a
          toast says so. Cancel — available any time pre-save. */}
      {(onBackground || onCancel) && !finalising && !serverFailed && (
        <div style={{ textAlign: 'center', marginTop: 8, display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          {onBackground && (
            <button
              onClick={onBackground}
              title={t('generator.background.buttonHint')}
              style={{
                padding: '8px 14px', borderRadius: 8,
                border: '1px solid rgba(127,75,246,0.35)',
                background: 'rgba(127,75,246,0.10)', color: 'rgba(127,75,246,0.95)',
                fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              {t('generator.background.button')}
            </button>
          )}
          {onCancel && <button
            onClick={onCancel}
            style={{
              padding: '8px 14px', borderRadius: 8, border: 'none',
              background: '#FFFFFF', color: 'rgba(27,37,80,0.475)',
              fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            {t('common:actions.cancel')}
          </button>}
        </div>
      )}

      <style>{`
        @keyframes pulseDot {
          0%, 100% { box-shadow: 0 0 0 0 rgba(127,75,246,0.5); }
          50%      { box-shadow: 0 0 0 6px rgba(127,75,246,0); }
        }
        .pgShimmer {
          position: absolute; inset: 0;
          background: linear-gradient(105deg, transparent 38%, rgba(127,75,246,0.10) 50%, transparent 62%);
          background-size: 240% 100%;
        }
        .pgTip { opacity: 1; }
        @media (prefers-reduced-motion: no-preference) {
          .pgShimmer { animation: pgSweep 2.6s ease-in-out infinite; }
          .pgTip { animation: pgTipIn 0.5s ease; }
        }
        @keyframes pgSweep { 0% { background-position: 130% 0; } 100% { background-position: -130% 0; } }
        @keyframes pgTipIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
      `}</style>
    </div>
  );
}
